/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiSkeletonLoading,
  EuiSkeletonRectangle,
  EuiSkeletonText,
  EuiSpacer,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

// eslint-disable-next-line import/no-restricted-paths
import RolePanel from '@/components/Users/RoleAssignmentsPanel/RolePanel'

import CCMAssignmentsSearchableTable from './CCMAssignmentsSearchableTable'
import { CCMRoleDropdown } from './CCMRoleDropdown'

import type { FC } from 'react'
import type { CCMRolePanelProps } from './types'

const messages = defineMessages({
  connectedClusterAccess: {
    id: 'roles-assignments.ccm-access',
    defaultMessage: 'Connected cluster access',
  },
})

const CCMRolePanel: FC<CCMRolePanelProps> = ({
  roleAssignments,
  isChecked,
  roleSelectionOverride,
  selectedRoleForAllDeployments: selectedGlobalRole,
  isLoading = false,
  onChangeRoleForConnectedCluster,
  onAssignRoleForAllConnectedClusters,
  onUnassignRoleForAllConnectedClusters,
  onSwitchToggle,
}) => {
  const { formatMessage } = useIntl()

  const hasGlobalRoleSelectionOverride = selectedGlobalRole !== undefined

  const actualRoleAssignments = hasGlobalRoleSelectionOverride
    ? roleAssignments.map((roleAssignment) => ({
        ...roleAssignment,
        roles: [selectedGlobalRole],
      }))
    : roleAssignments

  const showPanelContent = isChecked && !roleSelectionOverride

  return (
    <RolePanel
      title={formatMessage(messages.connectedClusterAccess)}
      ariaLabel={formatMessage(messages.connectedClusterAccess)}
      description={
        <FormattedMessage
          id='roles-assignments.ccm-access-description'
          defaultMessage='Grant access to services for connected clusters.'
        />
      }
      isChecked={isChecked || roleSelectionOverride}
      isDisabled={roleSelectionOverride}
      testId='cloud-connected-cluster-access'
      onSwitchToggled={onSwitchToggle}
    >
      <EuiSkeletonLoading
        isLoading={showPanelContent && isLoading}
        contentAriaLabel={formatMessage({
          id: 'roles-assignments.ccm-access-loading',
          defaultMessage: 'Loading connected cluster access roles',
        })}
        loadedContent={
          showPanelContent && (
            <EuiFlexGroup direction='column' gutterSize='l'>
              <EuiSpacer size='s' />
              <EuiFlexItem>
                <FormattedMessage
                  id='roles-assignments.ccm-access.description'
                  defaultMessage='Set a role for all Connected clusters, or a specific role individually for each cluster.'
                />
              </EuiFlexItem>
              <CCMRoleDropdown
                selectedGlobalRole={selectedGlobalRole}
                onRoleChange={(role) => {
                  if (role === undefined) {
                    onUnassignRoleForAllConnectedClusters()
                  } else {
                    onAssignRoleForAllConnectedClusters(role)
                  }
                }}
              />
              <CCMAssignmentsSearchableTable
                roleAssignments={actualRoleAssignments}
                isDisabled={hasGlobalRoleSelectionOverride}
                onChangeCCMRoleAssignments={onChangeRoleForConnectedCluster}
              />
            </EuiFlexGroup>
          )
        }
        loadingContent={
          <EuiFlexGroup direction='column' gutterSize='l'>
            <EuiSpacer size='s' />
            <EuiSkeletonRectangle height={20} width={668} />
            <EuiSkeletonRectangle height={60} width={352} />
            <EuiSkeletonRectangle height={40} width={688} />
            <EuiSkeletonText lines={3} />
          </EuiFlexGroup>
        }
      />
    </RolePanel>
  )
}

export default CCMRolePanel
